import { type Routes } from '@/router';
import { WooChevronRight } from '@nosinovacao/fe-icons';
import { cn } from '@nosinovacao/nosid-mfe-common';
import { Link, type LinkProps } from '@tanstack/react-router';
import { type ComponentType, type ReactNode } from 'react';

export function NavItemWooDark<
  T extends keyof Routes,
  TParams extends Routes[T]['params'],
  TSearch extends Routes[T]['search'],
>({
  icon: Icon,
  active,
  title,
  route,
  onClick,
  canClickWhenActive = false,
  'data-tid': dataTid,
}: Readonly<{
  active: boolean;
  icon: ComponentType<{ width: number; height: number }>;
  title: ReactNode;
  route?: {
    path: T;
    params: TParams;
    search: TSearch;
  };
  onClick: () => void;
  canClickWhenActive?: boolean;
  'data-tid'?: string;
}>) {
  const canClick = !active || canClickWhenActive;
  const handleClick = () => {
    if (canClick) onClick();
  };
  const className = cn(
    'relative isolate flex w-full items-center gap-3 rounded-l-lg px-4 py-6 text-nos-neutral-0 transition-colors duration-300 [&>*]:shrink-0',
    'after:absolute after:left-0 after:top-1/2 after:z-10 after:h-0 after:w-1 after:-translate-y-1/2 after:rounded-r-2xl after:bg-woo-green after:transition-[height] after:duration-300',
    '-outline-offset-1 data-[active=true]:bg-nos-neutral-950 data-[active=true]:after:h-12',
    'focus-visible:outline-none data-[active=false]:hover:bg-nos-neutral-900/50 data-[active=false]:focus-visible:bg-nos-neutral-900/50',
    active && !canClickWhenActive && 'cursor-default',
  );
  const children = (
    <>
      <Icon width={24} height={24} />
      <span className="font-medium font-ui-8">{title}</span>
      <WooChevronRight
        data-active={active}
        className="ml-auto transition-opacity duration-300 data-[active=true]:opacity-0"
        width={24}
        height={24}
      />
    </>
  );

  return (
    <li>
      {route ? (
        <Link
          {...({
            to: route.path,
            params: route.params,
            search: route.search,
          } as LinkProps)}
          data-tid={dataTid}
          data-active={active}
          onClick={handleClick}
          className={className}
        >
          {children}
        </Link>
      ) : (
        <button
          type="button"
          data-tid={dataTid}
          data-active={active}
          onClick={handleClick}
          className={className}
        >
          {children}
        </button>
      )}
    </li>
  );
}
